import { FC, ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { formatDateTime } from '@clatter/platform';
import { useAssetDownload, useUpload } from '../../hooks';
import { CircularLoader } from '../../shared';
import { useGetAssetVersionsQuery } from '../../services';
import {
  FolderManagerActionTypes,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerMimeTypes,
} from '../../interfaces';
import { getLongPullUIState } from '../../store/UI/longPull.slice';

import { FileDropContainer } from '../FolderManager/Components/FileDropContainer';
import { FolderManagerItemActions } from '../FolderManager/Components/FolderManagerItemActions';
import { getIconForMimeType } from '../../helpers';

interface IAssetVersionManager {
  item: FolderManagerItem;
  selectedFolderId: number;
}

const StyledContainer = styled.div`
  .content {
    display: flex;
    flex-direction: column;
    width: 98%;
    min-height: 30vh;
    margin: 1rem auto;
    justify-content: flex-start;
    gap: 1rem;
  }

  .list {
    flex: 1;
    height: 100%;
    max-height: 30vh;
    overflow: auto;
  }
`;

const StyledNotificationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;

  p {
    margin: 0;
  }
`;

interface IVersionsList {
  item: FolderManagerItem;
  onAction: (action: any) => void;
  data: IAssetVersion[] | undefined;
  isLoading: boolean;
}

interface IAssetVersionManagerNotification {
  message: string | ReactNode;
  showLoader: boolean;
}

const StyledVersionsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  height: 100%;
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  li {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    padding: 0.4rem;

    .description {
      flex: 1;
    }

    &:hover {
      background-color: rgba(5, 5, 5, 0.05);
    }
  }
`;

const VersionsList: FC<IVersionsList> = ({ data, item, onAction, isLoading }) => {
  const isSlide = item?.type === FolderManagerMimeTypes.SLIDE;

  if (isSlide && (!item?.itemData?.type || !item?.itemData?.id)) {
    console.error('VersionsList: slide item is missing itemData.type or itemData.id');
    return null;
  }

  const resolvedType = isSlide ? item.itemData.type : item.type;
  const resolvedId = isSlide ? item.itemData.id : item.id;

  if (isLoading) {
    return <CircularLoader />;
  }

  return (
    <StyledVersionsList>
      {(data || []).map((versionItem) => (
        <li key={versionItem.ID}>
          <div>{getIconForMimeType(resolvedType)}</div>

          <div className="description">
            <h4>{versionItem.FileName}</h4>
            <small>{formatDateTime(versionItem.CreatedAt)}</small>
          </div>

          <div className="actions">
            <FolderManagerItemActions
              actions={[
                {
                  name: FolderManagerItemActionNames.DOWNLOAD,
                  type: FolderManagerItemActionTypes.SINGLE,
                },
              ]}
              item={{ type: resolvedType, id: resolvedId, versionId: versionItem.ID } as FolderManagerItem}
              onAction={onAction}
            />
          </div>
        </li>
      ))}
    </StyledVersionsList>
  );
};

export const AssetVersionManager: FC<IAssetVersionManager> = ({ item, selectedFolderId }) => {
  const { uploadFiles } = useUpload();
  const { downloadAssetVersionById } = useAssetDownload();

  const [notifications, setNotifications] = useState<IAssetVersionManagerNotification[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadNotificationId, setUploadNotificationId] = useState<string | null>(null);

  // for slides, resolve the parent asset's ID and MIME type from itemData
  const isSlide = item?.type === FolderManagerMimeTypes.SLIDE;

  if (isSlide && (!item?.itemData?.type || !item?.itemData?.id)) {
    console.error('AssetVersionManager: slide item is missing itemData.type or itemData.id');
  }

  const assetId = isSlide ? item?.itemData?.id : item?.id;
  const assetMimeType = isSlide ? item?.itemData?.type : item?.type;

  const { data: assetVersions, isLoading, refetch } = useGetAssetVersionsQuery({ assetId }, { skip: !assetId });

  // Watch the long-pull UI store for upload completion, then refetch versions
  const longPullState = useSelector(getLongPullUIState);

  useEffect(() => {
    if (!uploadNotificationId) return;

    const uploadItem = (longPullState?.['global'] || []).find(
      (entry: ILongPullUIItem) => entry.id === uploadNotificationId,
    );

    if (uploadItem?.isCompleted) {
      refetch();
      setNotifications([]);
      setUploadNotificationId(null);
    }
  }, [longPullState, uploadNotificationId, refetch]);

  const handleUpload = async (files: FileList) => {
    setErrorMessage(null);

    if (files?.length !== 1) {
      setErrorMessage('Error: Please upload only one file.');
      console.error('Error, please provide only one file for upload!');
      return;
    }

    if (files[0]?.type !== assetMimeType) {
      setErrorMessage(`Upload failed: The file types do not match!`);
      console.error('File types does not match!');
      return;
    }

    setNotifications([
      {
        message: (
          <>
            Uploading <strong>"{files?.[0].name}"</strong> file
          </>
        ),
        showLoader: true,
      },
    ]);

    const uploadResponse = await uploadFiles({
      files: files,
      folderId: selectedFolderId,
      asset_id: assetId,
    });

    if (uploadResponse?.error) {
      setErrorMessage('Error uploading new asset version');
      setNotifications([]);
      return;
    }

    // Track the upload notification — the effect above will refetch when it completes
    setUploadNotificationId(uploadResponse?.notificationItemId ?? null);
    setErrorMessage(null);
  };

  const handleAction = async (action: any) => {
    switch (action?.actionType) {
      case FolderManagerActionTypes.DOWNLOAD:
        const { id, versionId } = action?.payload || {};

        if (!id || Number.isNaN(id)) {
          console.error('Error downloading asset version. No or invalid asset Id provided');
          return;
        }

        if (!versionId || Number.isNaN(versionId)) {
          console.error('Error downloading asset version. No or invalid version Id provided');
          return;
        }

        downloadAssetVersionById({ assetId: id, assetVersionId: versionId });

        break;
      default:
        console.log('AssetVersionManager: Unsupported action');
    }
  };

  if (!item) {
    console.error('Error no item provided');
    return null;
  }

  return (
    <StyledContainer>
      <FileDropContainer isItemContainer onUpload={handleUpload}>
        <div className="content">
          <div>
            <h3>
              Manage versions for: <strong>{item.name}</strong>.
            </h3>
            <p>Drop files onto this dialog to upload a new version.</p>
          </div>

          <div className="list">
            <VersionsList data={assetVersions} item={item} onAction={handleAction} isLoading={isLoading} />
          </div>

          <div className="notification-container">
            {notifications?.map((notificationItem, index) => (
              <StyledNotificationItem key={index}>
                <p>{notificationItem.message}</p> {notificationItem.showLoader && <CircularLoader size={30} />}
              </StyledNotificationItem>
            ))}

            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
          </div>
        </div>
      </FileDropContainer>
    </StyledContainer>
  );
};
